import type { OwnMapOwnership, OwnMapTeam } from './ownMapTypes';
import { FIPS_ALIASES } from './ownMapGeo';

export interface EmpireStats {
  team: OwnMapTeam;
  counties: number;
  population: number;
  countyShare: number;
  popShare: number;
  rankByCounties: number;
  rankByPopulation: number;
  empiresWithLand: number;
  crownJewel: { fips: string; name: string; population: number } | null;
}

function metricForFips(
  fips: string,
  table: Record<string, number | null | undefined>
): number {
  const id = String(fips).padStart(5, '0');
  let value = table[id];
  if (value == null) {
    const alt = FIPS_ALIASES[id];
    if (alt) value = table[alt];
  }
  return Number(value || 0);
}

function popForFips(fips: string, popByFips: Record<string, number | null>): number {
  return metricForFips(fips, popByFips);
}

/** Aggregate empire standings from already-loaded own-map ownership + population. */
export function buildEmpireStats(
  ownership: OwnMapOwnership | null,
  teamId: number,
  popByFips: Record<string, number | null>,
  countyLabels: Record<string, string> = {}
): EmpireStats | null {
  if (!ownership) return null;
  const team = ownership.teams[teamId];
  if (!team) return null;

  // Deduplicate FIPS aliases so we don't double-count geometry mirrors.
  const canonical = new Set<string>();
  const teamCounties: string[] = [];
  for (const [fips, ownerId] of Object.entries(ownership.ownership)) {
    if (Number(ownerId) !== Number(teamId)) continue;
    const id = String(fips).padStart(5, '0');
    const alias = FIPS_ALIASES[id];
    const key = alias && ownership.ownership[alias] != null ? (id < alias ? id : alias) : id;
    if (canonical.has(key)) continue;
    canonical.add(key);
    teamCounties.push(key);
  }

  const byTeam = new Map<number, { counties: number; population: number }>();
  const seen = new Set<string>();
  let totalCounties = 0;
  let totalPop = 0;

  for (const [fips, ownerId] of Object.entries(ownership.ownership)) {
    const id = String(fips).padStart(5, '0');
    const alias = FIPS_ALIASES[id];
    const key = alias && ownership.ownership[alias] != null ? (id < alias ? id : alias) : id;
    if (seen.has(key)) continue;
    seen.add(key);
    const tid = Number(ownerId);
    const pop = popForFips(key, popByFips);
    totalCounties += 1;
    totalPop += pop;
    const cur = byTeam.get(tid) || { counties: 0, population: 0 };
    cur.counties += 1;
    cur.population += pop;
    byTeam.set(tid, cur);
  }

  const mine = byTeam.get(Number(teamId)) || { counties: 0, population: 0 };
  const rankedCounties = Array.from(byTeam.entries()).sort(
    (a, b) =>
      b[1].counties - a[1].counties ||
      b[1].population - a[1].population ||
      a[0] - b[0]
  );
  const rankedPop = Array.from(byTeam.entries()).sort(
    (a, b) =>
      b[1].population - a[1].population ||
      b[1].counties - a[1].counties ||
      a[0] - b[0]
  );

  let crownJewel: EmpireStats['crownJewel'] = null;
  for (const fips of teamCounties) {
    const pop = popForFips(fips, popByFips);
    if (!crownJewel || pop > crownJewel.population) {
      crownJewel = {
        fips,
        name: countyLabels[fips] || countyLabels[String(fips).padStart(5, '0')] || `County ${fips}`,
        population: pop,
      };
    }
  }

  return {
    team,
    counties: mine.counties,
    population: mine.population,
    countyShare: totalCounties > 0 ? mine.counties / totalCounties : 0,
    popShare: totalPop > 0 ? mine.population / totalPop : 0,
    rankByCounties: rankedCounties.findIndex(([id]) => Number(id) === Number(teamId)) + 1 || 0,
    rankByPopulation: rankedPop.findIndex(([id]) => Number(id) === Number(teamId)) + 1 || 0,
    empiresWithLand: byTeam.size,
    crownJewel,
  };
}

export function formatCompactPop(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}K`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

export function formatSqMi(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M mi²`;
  if (n >= 10_000) return `${Math.round(n / 1000)}K mi²`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K mi²`;
  return `${Math.round(n).toLocaleString()} mi²`;
}

export interface EmpireRankRow {
  teamId: number;
  team: OwnMapTeam;
  counties: number;
  share: number;
  population: number;
  areaSqMi: number;
}

/** Rank empires by county share for broadcast standings. */
export function rankEmpiresByCounties(
  ownership: OwnMapOwnership | null,
  popByFips: Record<string, number | null> = {},
  areaSqMiByFips: Record<string, number> = {}
): EmpireRankRow[] {
  if (!ownership) return [];
  const counts = new Map<number, { counties: number; population: number; areaSqMi: number }>();
  const seen = new Set<string>();
  let total = 0;
  for (const [fips, ownerId] of Object.entries(ownership.ownership)) {
    const id = String(fips).padStart(5, '0');
    const alias = FIPS_ALIASES[id];
    const key = alias && ownership.ownership[alias] != null ? (id < alias ? id : alias) : id;
    if (seen.has(key)) continue;
    seen.add(key);
    total += 1;
    const tid = Number(ownerId);
    const cur = counts.get(tid) || { counties: 0, population: 0, areaSqMi: 0 };
    cur.counties += 1;
    cur.population += popForFips(key, popByFips);
    cur.areaSqMi += metricForFips(key, areaSqMiByFips);
    counts.set(tid, cur);
  }
  return Array.from(counts.entries())
    .map(([teamId, stats]) => {
      const team = ownership.teams[teamId];
      if (!team) return null;
      return {
        teamId,
        team,
        counties: stats.counties,
        share: total > 0 ? stats.counties / total : 0,
        population: stats.population,
        areaSqMi: stats.areaSqMi,
      };
    })
    .filter((r): r is EmpireRankRow => !!r)
    .sort((a, b) => b.counties - a.counties || a.teamId - b.teamId);
}
